import { useEffect, useRef, useState } from 'react';
import { useStore } from '../state/store';
import { Icon } from '../ui';

interface Props {
  onManage: () => void;
}

/**
 * The persistent scenario switcher in the top bar. One click reveals a
 * dropdown of every non-archived scenario. The active scenario is visible at
 * rest so the user always knows which plan the dashboard is showing.
 */
export function ScenarioSwitcher({ onManage }: Props) {
  const { scenarios, activeScenario, setActiveScenario, compareScenarioIds, toggleCompareScenario } =
    useStore();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent) {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    window.addEventListener('mousedown', onClick);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onClick);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const visible = scenarios.filter(s => !s.archived);

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-2 rounded-xl border border-ink-200 bg-white px-3 py-1.5 text-sm font-semibold text-ink-900 shadow-sm transition hover:border-ink-300 hover:bg-ink-50 dark:border-ink-700 dark:bg-ink-800 dark:text-ink-50 dark:hover:border-ink-600 dark:hover:bg-ink-700"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <span
          className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-sm"
          style={{ backgroundColor: `${activeScenario.color}22`, color: activeScenario.color }}
          aria-hidden
        >
          {activeScenario.icon}
        </span>
        <span className="max-w-[8rem] truncate">{activeScenario.name}</span>
        {compareScenarioIds.length > 0 && (
          <span
            className="inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-accent-600 px-1 text-[10px] font-bold text-white"
            title={`${compareScenarioIds.length} comparing`}
          >
            +{compareScenarioIds.length}
          </span>
        )}
        <Icon name="chevron-down" size={14} className="text-ink-400" />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 z-40 mt-1 w-72 overflow-hidden rounded-2xl border border-ink-200 bg-white shadow-card dark:border-ink-700 dark:bg-ink-800"
        >
          <div className="border-b border-ink-100 bg-ink-50/60 px-3 py-2 dark:border-ink-700 dark:bg-ink-800/50">
            <div className="text-[10px] font-semibold uppercase tracking-wide text-ink-500 dark:text-ink-300">
              Scenarios
            </div>
            <div className="mt-0.5 text-[11px] text-ink-500 dark:text-ink-300">
              Pick an active plan. Toggle ⊕ to overlay on charts.
            </div>
          </div>
          <ul className="max-h-80 overflow-y-auto py-1">
            {visible.map(s => {
              const isActive = s.id === activeScenario.id;
              const inCompare = compareScenarioIds.includes(s.id);
              return (
                <li key={s.id} className="flex items-center gap-1 px-1">
                  <button
                    type="button"
                    role="menuitemradio"
                    aria-checked={isActive}
                    onClick={async () => {
                      await setActiveScenario(s.id);
                      setOpen(false);
                    }}
                    className={`flex flex-1 items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm transition ${
                      isActive
                        ? 'bg-accent-50 text-accent-900 dark:bg-accent-700/20 dark:text-accent-500'
                        : 'hover:bg-ink-50 dark:hover:bg-ink-700'
                    }`}
                  >
                    <span
                      className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-sm"
                      style={{ backgroundColor: `${s.color}22`, color: s.color }}
                      aria-hidden
                    >
                      {s.icon}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-semibold">
                        {s.name}
                      </span>
                      <span className="block truncate text-[11px] text-ink-500 dark:text-ink-300">
                        {s.events.length} event{s.events.length === 1 ? '' : 's'}
                        {isActive ? ' · active' : ''}
                      </span>
                    </span>
                    {isActive && <Icon name="check" size={14} className="text-accent-600" />}
                  </button>
                  <button
                    type="button"
                    onClick={async () => {
                      if (isActive) return;
                      await toggleCompareScenario(s.id);
                    }}
                    disabled={isActive}
                    title={isActive ? 'Active scenario' : inCompare ? 'Remove from compare' : 'Add to compare'}
                    className={`mr-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-xs font-bold transition ${
                      inCompare
                        ? 'bg-accent-600 text-white'
                        : 'bg-ink-100 text-ink-500 hover:bg-ink-200 dark:bg-ink-700 dark:text-ink-300 dark:hover:bg-ink-600'
                    } ${isActive ? 'opacity-30' : ''}`}
                  >
                    ⊕
                  </button>
                </li>
              );
            })}
          </ul>
          <div className="border-t border-ink-100 p-1 dark:border-ink-700">
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                onManage();
              }}
              className="flex w-full items-center justify-between rounded-lg px-3 py-2 text-sm font-semibold text-accent-600 hover:bg-ink-50 dark:text-accent-500 dark:hover:bg-ink-700"
            >
              <span>Manage scenarios</span>
              <Icon name="chevron-right" size={14} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
